import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import { Truck, Package, Plane, MapPin, Home, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Public order tracking page — white-label timeline for /commande/{id}/suivi
 * No supplier / courier branding shown. Uses TrackingMore data via
 * /api/orders/{id}/tracking, mapped to our 5-step journey.
 */
const STEPS = [
  { key: "shipped",          label: "Expédié",              Icon: Package,       tone: "sage" },
  { key: "in_transit",       label: "En route",             Icon: Plane,         tone: "pedablue" },
  { key: "arrived_country",  label: "Arrivé dans le pays",  Icon: MapPin,        tone: "coral" },
  { key: "out_for_delivery", label: "En cours de livraison", Icon: Truck,        tone: "coral" },
  { key: "delivered",        label: "Livré",                Icon: CheckCircle2,  tone: "sage" },
];
const STEP_ORDER = STEPS.map((s) => s.key);

function stepIndex(status) {
  const i = STEP_ORDER.indexOf(status);
  return i < 0 ? 0 : i;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function OrderTracking() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asked, setAsked] = useState(!!params.get("email"));

  async function fetchData(overrideEmail) {
    setLoading(true);
    setError(null);
    try {
      const q = overrideEmail ? `?email=${encodeURIComponent(overrideEmail)}` : "";
      const r = await api.get(`/orders/${orderId}/tracking${q}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de charger le suivi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (asked) fetchData(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, asked]);

  return (
    <div className="min-h-screen bg-ivory">
      <Seo
        title={`Suivi de commande — Orthophonie Online`}
        description="Suivez votre commande en temps réel."
        noindex
      />
      <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
        <Link to="/" className="text-xs text-ink-soft hover:text-ink underline">← Retour à l&apos;accueil</Link>
        <p className="text-xs uppercase tracking-[0.3em] text-pedablue mt-6 mb-2">Suivi de commande</p>
        <h1 className="font-display text-3xl md:text-4xl mb-2">Où est votre colis ?</h1>
        <p className="text-ink-soft text-sm mb-8">
          Commande <code className="bg-ivory-200 px-2 py-0.5 rounded text-[11px]">{orderId?.slice(0, 8)}…</code> ·
          Mise à jour en temps réel via nos transporteurs.
        </p>

        {!asked && (
          <form
            onSubmit={(e) => { e.preventDefault(); setAsked(true); }}
            className="bg-white rounded-3xl border border-ink/10 p-6 mb-6"
            data-testid="tracking-email-form"
          >
            <label className="block text-sm font-medium mb-2">
              Pour votre sécurité, saisissez l&apos;email utilisé lors de la commande
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="input flex-1"
                data-testid="tracking-email-input"
              />
              <button type="submit" className="btn-primary" data-testid="tracking-email-submit">
                Voir le suivi
              </button>
            </div>
          </form>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16 text-ink-soft">
            <Loader2 className="animate-spin me-2" size={20} />
            Récupération du suivi…
          </div>
        )}

        {error && (
          <div className="bg-coral/10 border border-coral/30 rounded-2xl p-4 text-sm text-coral-dark" data-testid="tracking-error">
            {error}
            <button onClick={() => { setAsked(false); setError(null); }} className="underline ms-2">Réessayer</button>
          </div>
        )}

        {data && data.trackings && data.trackings.length === 0 && !loading && (
          <div className="bg-white rounded-3xl border border-ink/10 p-8 text-center" data-testid="tracking-not-yet">
            <Package size={40} className="mx-auto text-ink/30 mb-3" />
            <p className="font-display text-xl mb-2">Votre commande est confirmée !</p>
            <p className="text-sm text-ink-soft">
              Elle est en cours de préparation. Vous recevrez un email dès qu&apos;elle sera expédiée
              avec le suivi en temps réel.
            </p>
          </div>
        )}

        {data && data.trackings && data.trackings.map((t, idx) => (
          <TrackingCard key={idx} tracking={t} index={idx} total={data.trackings.length} />
        ))}

        <div className="mt-10 text-center">
          <p className="text-xs text-ink-soft mb-2">Un souci avec votre colis ?</p>
          <Link to="/contact" className="btn-outline text-sm" data-testid="tracking-contact-link">
            Contacter le service client
          </Link>
        </div>
      </div>
    </div>
  );
}

function TrackingCard({ tracking, index, total }) {
  const currentIdx = stepIndex(tracking.status || "shipped");
  const isException = tracking.status === "exception";
  return (
    <div className="bg-white rounded-3xl border border-ink/10 p-6 md:p-8 mb-4" data-testid={`tracking-card-${index}`}>
      {total > 1 && (
        <p className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-3">
          Colis {index + 1} sur {total}
        </p>
      )}
      <p className="text-xs text-ink-soft">Numéro de suivi</p>
      <p className="font-mono text-sm mb-4" data-testid={`tracking-code-${index}`}>{tracking.code}</p>

      {isException ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 flex gap-3 items-start">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Un événement inhabituel a été détecté</p>
            <p className="text-xs text-ink-soft mt-1">
              Pas de panique — on est déjà en train de regarder. Contactez-nous si besoin.
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <div className="hidden md:flex justify-between items-start relative">
            <div className="absolute top-6 left-6 right-6 h-1 bg-ivory-200 rounded-full">
              <div
                className="h-full bg-gradient-to-r from-sage to-coral rounded-full transition-all"
                style={{ width: `${(currentIdx / (STEPS.length - 1)) * 100}%` }}
              />
            </div>
            {STEPS.map((s, i) => {
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.key} className="relative z-10 flex flex-col items-center text-center" style={{ width: "18%" }}>
                  <div
                    className={
                      "w-12 h-12 rounded-full flex items-center justify-center transition-all " +
                      (done
                        ? active
                          ? "bg-coral text-white ring-4 ring-coral/25 scale-110"
                          : "bg-sage text-white"
                        : "bg-ivory-200 text-ink/40")
                    }
                    data-testid={`tracking-step-${s.key}-${i <= currentIdx ? "done" : "todo"}`}
                  >
                    <s.Icon size={20} />
                  </div>
                  <p className={"text-[11px] mt-2 leading-tight " + (done ? "text-ink font-medium" : "text-ink/40")}>
                    {s.label}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="md:hidden space-y-3">
            {STEPS.map((s, i) => {
              const done = i <= currentIdx;
              return (
                <div key={s.key} className={"flex items-center gap-3 " + (done ? "" : "opacity-40")}>
                  <div className={"w-10 h-10 rounded-full flex items-center justify-center shrink-0 " + (done ? "bg-sage text-white" : "bg-ivory-200")}>
                    <s.Icon size={18} />
                  </div>
                  <p className={"text-sm " + (done ? "font-medium" : "")}>{s.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tracking.checkpoints && tracking.checkpoints.length > 0 && (
        <div className="border-t border-ink/10 pt-4">
          <p className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-3">Détail du parcours</p>
          <ul className="space-y-3">
            {tracking.checkpoints.slice(-8).reverse().map((cp, i) => (
              <li key={i} className="flex gap-3 items-start" data-testid={`tracking-checkpoint-${index}-${i}`}>
                <div className="w-2 h-2 rounded-full bg-pedablue mt-2 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-ink">{cp.message}</p>
                  <p className="text-xs text-ink-soft mt-0.5">
                    {fmtDate(cp.at)}{cp.location ? ` · ${cp.location}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(!tracking.checkpoints || tracking.checkpoints.length === 0) && !isException && (
        <p className="text-xs text-ink-soft italic border-t border-ink/10 pt-4">
          Le premier scan du transporteur peut prendre 24-48h. On vous notifiera par email dès que ça bouge.
        </p>
      )}
    </div>
  );
}
